import { ChainRow } from "../../../api/Tasks";

// ChainLink is one "blocked by" entry of a row. "prev" is the row before it, whichever
// row that is after a reorder; "row" names one row, and stays with it; "task" is an
// existing task.
export type ChainLink =
    | { kind: "prev" }
    | { kind: "row"; key: string }
    | { kind: "task"; id: number; title: string };

export type ChainFile = {
    id: number;
    name: string;
}

// DraftRow is one task of the chain being made. key stays with the row through a
// reorder; its seq is its place in the list.
export type DraftRow = {
    key: string;
    title: string;
    dueDate: string;
    description: string;
    files: ChainFile[];
    assignedTo: number[];
    blockedBy: ChainLink[];
    expanded: boolean;
}

let nextKey = 1;

export function newRow(assignedTo: number[], first: boolean): DraftRow {
    return {
        key: "row-" + nextKey++,
        title: "",
        dueDate: "",
        description: "",
        files: [],
        assignedTo: assignedTo,

        // a new row waits on the one before it, which is how a chain reads by default
        blockedBy: first ? [] : [{ kind: "prev" }],
        expanded: false,
    };
}

// linkSeq is the seq a link points at, or 0 for one that points at no row
export function linkSeq(rows: DraftRow[], index: number, link: ChainLink) {
    if (link.kind === "prev") return index > 0 ? index : 0;
    if (link.kind === "row") return rows.findIndex(row => row.key === link.key) + 1;
    return 0;
}

// linkLabel names a link on its chip
export function linkLabel(rows: DraftRow[], index: number, link: ChainLink) {
    if (link.kind === "task") return "#" + link.id + " " + link.title;

    const seq = linkSeq(rows, index, link);
    return seq ? seq + " " + (rows[seq - 1].title || "Untitled") : "";
}

// removeRow takes the row out, and out of every other row's "blocked by"
export function removeRow(rows: DraftRow[], key: string): DraftRow[] {
    return rows
        .filter(row => row.key !== key)
        .map(row => ({
            ...row,
            blockedBy: row.blockedBy.filter(link => link.kind !== "row" || link.key !== key),
        }));
}

// toChainRows is what the server is sent
export function toChainRows(rows: DraftRow[]): ChainRow[] {
    return rows.map((row, index) => ({
        seq: index + 1,
        title: row.title.trim(),
        dueDate: row.dueDate || null,
        description: row.description,
        files: row.files.map(file => file.id),
        assignedTo: row.assignedTo,
        blockedBySeq: Array.from(new Set(row.blockedBy
            .map(link => linkSeq(rows, index, link))
            .filter(seq => seq > 0 && seq !== index + 1))),
        blockedByTask: row.blockedBy.flatMap(link => link.kind === "task" ? [link.id] : []),
    }));
}
